import React, { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Building2, Users, Plus, Mail, Shield, FileText, Receipt, Save, Search, Package, UserCircle } from "lucide-react";
import axios from "axios";

const Settings = () => {
  const { user, token, fetchUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: "",
    org_number: "",
    address: "",
    city: "",
    postal_code: ""
  });
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "user"
  });
  const [companySettings, setCompanySettings] = useState({
    name: "",
    org_number: "",
    vat_number: "",
    address: "",
    postal_code: "",
    city: "",
    phone: "",
    email: "",
    website: "",
    bankgiro: "",
    plusgiro: "",
    bank_account: "",
    swish: ""
  });
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoice_prefix: "F",
    next_invoice_number: 1,
    default_payment_terms: 30,
    default_vat_rate: 25.0,
    invoice_footer: "",
    reminder_fee: 60.0,
    late_interest_rate: 8.0,
    bank_account: "",
    bankgiro: "",
    plusgiro: "",
    swish: ""
  });
  const [quoteSettings, setQuoteSettings] = useState({
    quote_prefix: "O",
    next_quote_number: 1,
    default_validity_days: 30,
    quote_footer: ""
  });
  
  const [articleSettings, setArticleSettings] = useState({
    article_prefix: "ART",
    next_article_number: 1,
    number_format: "prefix_number", // prefix_number, number_only
    default_vat_rate: 25,
    default_unit: "st",
    default_account: "3010"
  });
  
  const [customerSettings, setCustomerSettings] = useState({
    customer_prefix: "K",
    next_customer_number: 1,
    number_format: "prefix_number",
    default_payment_terms: 30,
    default_payment_type: "net_days"
  });

  useEffect(() => {
    fetchData();
  }, [user?.active_company_id]);

  const fetchData = async () => {
    try {
      const [companiesRes, usersRes] = await Promise.all([
        axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        user?.active_company_id 
          ? axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
          : Promise.resolve({ data: [] })
      ]);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);

      // Fetch settings
      if (user?.active_company_id) {
        const [companySettingsRes, invoiceSettingsRes, quoteSettingsRes, articleSettingsRes, customerSettingsRes] = await Promise.all([
          axios.get(`${API}/settings/company`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/invoice`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/quote`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/article`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/customer`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
        ]);
        setCompanySettings(prev => ({ ...prev, ...companySettingsRes.data }));
        setInvoiceSettings(prev => ({ ...prev, ...invoiceSettingsRes.data }));
        setQuoteSettings(prev => ({ ...prev, ...quoteSettingsRes.data }));
        setArticleSettings(prev => ({ ...prev, ...articleSettingsRes.data }));
        setCustomerSettings(prev => ({ ...prev, ...customerSettingsRes.data }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/companies`, companyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Företag skapat");
      fetchData();
      fetchUser();
      setCompanyDialogOpen(false);
      setCompanyForm({ name: "", org_number: "", address: "", city: "", postal_code: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa företag");
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("email", inviteForm.email);
      formData.append("role", inviteForm.role);
      
      await axios.post(`${API}/users/invite`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Inbjudan skickad");
      fetchData();
      setInviteDialogOpen(false);
      setInviteForm({ email: "", role: "user" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte bjuda in användare");
    }
  };

  const lookupCompanyInfo = async (orgNumber = null) => {
    const searchOrg = orgNumber || companySettings.org_number;
    if (!searchOrg) {
      toast.error("Ange organisationsnummer");
      return;
    }
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API}/customers/lookup/${searchOrg}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanySettings(prev => ({
        ...prev,
        name: response.data.name || prev.name,
        address: response.data.address || prev.address,
        postal_code: response.data.postal_code || prev.postal_code,
        city: response.data.city || prev.city
      }));
      toast.success("Företagsinformation hämtad");
    } catch (error) {
      toast.error("Kunde inte hitta företaget");
    } finally {
      setLookupLoading(false);
    }
  };

  // Auto-lookup when org number is complete (10 digits) for company settings
  const handleCompanySettingsOrgChange = (value) => {
    setCompanySettings({ ...companySettings, org_number: value });
    const cleanOrg = value.replace(/[-\s]/g, '');
    if (cleanOrg.length === 10 && /^\d{10}$/.test(cleanOrg)) {
      lookupCompanyInfo(value);
    }
  };

  // Lookup for new company dialog
  const lookupNewCompanyInfo = async (orgNumber) => {
    if (!orgNumber) return;
    const cleanOrg = orgNumber.replace(/[-\s]/g, '');
    if (cleanOrg.length !== 10 || !/^\d{10}$/.test(cleanOrg)) return;
    
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API}/customers/lookup/${orgNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyForm(prev => ({
        ...prev,
        name: response.data.name || prev.name,
        address: response.data.address || prev.address,
        postal_code: response.data.postal_code || prev.postal_code,
        city: response.data.city || prev.city
      }));
      toast.success("Företagsinformation hämtad");
    } catch (error) {
      // Silent fail for auto-lookup
    } finally {
      setLookupLoading(false);
    }
  };

  const handleNewCompanyOrgChange = (value) => {
    setCompanyForm({ ...companyForm, org_number: value });
    lookupNewCompanyInfo(value);
  };

  const saveCompanySettings = async () => {
    try {
      await axios.put(`${API}/settings/company`, companySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Företagsinställningar sparade");
    } catch (error) {
      toast.error("Kunde inte spara inställningar");
    }
  };

  const saveInvoiceSettings = async () => {
    try {
      await axios.put(`${API}/settings/invoice`, invoiceSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Faktureringsinställningar sparade");
    } catch (error) {
      toast.error("Kunde inte spara inställningar");
    }
  };

  const saveQuoteSettings = async () => {
    try {
      await axios.put(`${API}/settings/quote`, quoteSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offertinställningar sparade");
    } catch (error) {
      toast.error("Kunde inte spara inställningar");
    }
  };

  const saveArticleSettings = async () => {
    try {
      await axios.put(`${API}/settings/article`, articleSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Artikelinställningar sparade");
    } catch (error) {
      toast.error("Kunde inte spara inställningar");
    }
  };

  const saveCustomerSettings = async () => {
    try {
      await axios.put(`${API}/settings/customer`, customerSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Kundinställningar sparade");
    } catch (error) {
      toast.error("Kunde inte spara inställningar");
    }
  };

  // Helper to generate preview number (no zero padding)
  const getNumberPreview = (prefix, nextNumber, format) => {
    if (format === "number_only") {
      return String(nextNumber);
    }
    return prefix + String(nextNumber);
  };

  const activeCompany = companies.find(c => c.id === user?.active_company_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="settings">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Inställningar</h1>
        <p className="text-slate-500 mt-1">Hantera företag, användare och inställningar</p>
      </div>

      <Tabs defaultValue="company-settings">
        <TabsList className="flex-wrap">
          <TabsTrigger value="company-settings" data-testid="tab-company-settings">
            <Building2 size={16} className="mr-2" />
            Företag
          </TabsTrigger>
          <TabsTrigger value="invoice-settings" data-testid="tab-invoice-settings">
            <Receipt size={16} className="mr-2" />
            Fakturering
          </TabsTrigger>
          <TabsTrigger value="quote-settings" data-testid="tab-quote-settings">
            <FileText size={16} className="mr-2" />
            Offerter
          </TabsTrigger>
          <TabsTrigger value="article-settings" data-testid="tab-article-settings">
            <Package size={16} className="mr-2" />
            Artiklar
          </TabsTrigger>
          <TabsTrigger value="customer-settings" data-testid="tab-customer-settings">
            <UserCircle size={16} className="mr-2" />
            Kunder
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">
            Mina företag
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users size={16} className="mr-2" />
            Användare
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company-settings" className="space-y-6 mt-6">
          <Card className="card-base" data-testid="company-settings-card">
            <CardHeader>
              <CardTitle className="text-lg">Företagsinställningar</CardTitle>
              <CardDescription>Uppgifter som visas på fakturor och offerter</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Org number lookup */}
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-2">
                  <Label>Organisationsnummer</Label>
                  <Input
                    value={companySettings.org_number || ""}
                    onChange={(e) => handleCompanySettingsOrgChange(e.target.value)}
                    placeholder="556123-4567"
                    data-testid="settings-org-number"
                  />
                  <p className="text-xs text-slate-500">Info hämtas automatiskt när du skriver 10 siffror</p>
                </div>
                <div className="pb-6">
                  <Button 
                    variant="outline" 
                    onClick={() => lookupCompanyInfo()}
                    disabled={lookupLoading}
                    data-testid="lookup-company-btn"
                  >
                    <Search size={16} className="mr-2" />
                    {lookupLoading ? "Söker..." : "Hämta info"}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Företagsnamn</Label>
                  <Input
                    value={companySettings.name || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, name: e.target.value })}
                    placeholder="Mitt Företag AB"
                    data-testid="settings-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Momsnummer</Label>
                  <Input
                    value={companySettings.vat_number || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, vat_number: e.target.value })}
                    placeholder="SE556123456701"
                    data-testid="settings-vat-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>E-post</Label>
                  <Input
                    value={companySettings.email || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, email: e.target.value })}
                    placeholder="info@foretag.se"
                    data-testid="settings-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefon</Label>
                  <Input
                    value={companySettings.phone || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, phone: e.target.value })}
                    placeholder="08-123 456 78"
                    data-testid="settings-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Webbplats</Label>
                  <Input
                    value={companySettings.website || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, website: e.target.value })}
                    placeholder="www.foretag.se"
                    data-testid="settings-website"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Adress</Label>
                <Input
                  value={companySettings.address || ""}
                  onChange={(e) => setCompanySettings({ ...companySettings, address: e.target.value })}
                  placeholder="Storgatan 1"
                  data-testid="settings-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Postnummer</Label>
                  <Input
                    value={companySettings.postal_code || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, postal_code: e.target.value })}
                    placeholder="111 22"
                    data-testid="settings-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ort</Label>
                  <Input
                    value={companySettings.city || ""}
                    onChange={(e) => setCompanySettings({ ...companySettings, city: e.target.value })}
                    placeholder="Stockholm"
                    data-testid="settings-city"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Betalningsinformation</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bankgiro</Label>
                    <Input
                      value={companySettings.bankgiro || ""}
                      onChange={(e) => setCompanySettings({ ...companySettings, bankgiro: e.target.value })}
                      placeholder="123-4567"
                      data-testid="settings-bankgiro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Plusgiro</Label>
                    <Input
                      value={companySettings.plusgiro || ""}
                      onChange={(e) => setCompanySettings({ ...companySettings, plusgiro: e.target.value })}
                      placeholder="12 34 56-7"
                      data-testid="settings-plusgiro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bankkonto</Label>
                    <Input
                      value={companySettings.bank_account || ""}
                      onChange={(e) => setCompanySettings({ ...companySettings, bank_account: e.target.value })}
                      placeholder="1234 12 34567"
                      data-testid="settings-bank-account"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Swish</Label>
                    <Input
                      value={companySettings.swish || ""}
                      onChange={(e) => setCompanySettings({ ...companySettings, swish: e.target.value })}
                      placeholder="123 456 78 90"
                      data-testid="settings-swish"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveCompanySettings} data-testid="save-company-settings">
                  <Save size={16} className="mr-2" />
                  Spara inställningar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoice Settings */}
        <TabsContent value="invoice-settings" className="space-y-6 mt-6">
          <Card className="card-base" data-testid="invoice-settings-card">
            <CardHeader>
              <CardTitle className="text-lg">Faktureringsinställningar</CardTitle>
              <CardDescription>Anpassa fakturanumrering och standardvärden</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fakturaprefix</Label>
                  <Input
                    value={invoiceSettings.invoice_prefix}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_prefix: e.target.value })}
                    placeholder="F"
                    data-testid="invoice-prefix"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nästa fakturanummer</Label>
                  <Input
                    type="number"
                    value={invoiceSettings.next_invoice_number}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, next_invoice_number: parseInt(e.target.value) })}
                    data-testid="invoice-next-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Standard betalningsvillkor (dagar)</Label>
                  <Input
                    type="number"
                    value={invoiceSettings.default_payment_terms}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, default_payment_terms: parseInt(e.target.value) })}
                    data-testid="invoice-payment-terms"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Standard momssats (%)</Label>
                  <Input
                    type="number"
                    value={invoiceSettings.default_vat_rate}
                    onChange={(e) => setInvoiceSettings({ ...invoiceSettings, default_vat_rate: parseFloat(e.target.value) })}
                    data-testid="invoice-vat-rate"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Påminnelser och ränta</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Påminnelseavgift (kr)</Label>
                    <Input
                      type="number"
                      value={invoiceSettings.reminder_fee}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, reminder_fee: parseFloat(e.target.value) })}
                      data-testid="invoice-reminder-fee"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Dröjsmålsränta (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={invoiceSettings.late_interest_rate}
                      onChange={(e) => setInvoiceSettings({ ...invoiceSettings, late_interest_rate: parseFloat(e.target.value) })}
                      data-testid="invoice-late-interest"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fakturafotnot</Label>
                <Input
                  value={invoiceSettings.invoice_footer || ""}
                  onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_footer: e.target.value })}
                  placeholder="Text som visas längst ned på alla fakturor"
                  data-testid="invoice-footer"
                />
              </div>

              <div className="flex justify-end">
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveInvoiceSettings} data-testid="save-invoice-settings">
                  <Save size={16} className="mr-2" />
                  Spara inställningar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quote Settings */}
        <TabsContent value="quote-settings" className="space-y-6 mt-6">
          <Card className="card-base" data-testid="quote-settings-card">
            <CardHeader>
              <CardTitle className="text-lg">Offertinställningar</CardTitle>
              <CardDescription>Anpassa offertnumrering och standardvärden</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Offertprefix</Label>
                  <Input
                    value={quoteSettings.quote_prefix}
                    onChange={(e) => setQuoteSettings({ ...quoteSettings, quote_prefix: e.target.value })}
                    placeholder="O"
                    data-testid="quote-prefix"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nästa offertnummer</Label>
                  <Input
                    type="number"
                    value={quoteSettings.next_quote_number}
                    onChange={(e) => setQuoteSettings({ ...quoteSettings, next_quote_number: parseInt(e.target.value) })}
                    data-testid="quote-next-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Standard giltighetstid (dagar)</Label>
                  <Input
                    type="number"
                    value={quoteSettings.default_validity_days}
                    onChange={(e) => setQuoteSettings({ ...quoteSettings, default_validity_days: parseInt(e.target.value) })}
                    data-testid="quote-validity-days"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Offertfotnot</Label>
                <Input
                  value={quoteSettings.quote_footer || ""}
                  onChange={(e) => setQuoteSettings({ ...quoteSettings, quote_footer: e.target.value })}
                  placeholder="Text som visas längst ned på alla offerter"
                  data-testid="quote-footer"
                />
              </div>

              <div className="flex justify-end">
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveQuoteSettings} data-testid="save-quote-settings">
                  <Save size={16} className="mr-2" />
                  Spara inställningar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Article Settings */}
        <TabsContent value="article-settings" className="space-y-6 mt-6">
          <Card className="card-base" data-testid="article-settings-card">
            <CardHeader>
              <CardTitle className="text-lg">Artikelinställningar</CardTitle>
              <CardDescription>Anpassa artikelnumrering och standardvärden för nya artiklar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Number Series Section */}
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <h4 className="font-medium text-indigo-900 flex items-center gap-2 mb-3">
                  <Package size={16} />
                  Nummerserie för artiklar
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Artikelprefix</Label>
                    <Input
                      value={articleSettings.article_prefix}
                      onChange={(e) => setArticleSettings({ ...articleSettings, article_prefix: e.target.value })}
                      placeholder="ART"
                      data-testid="article-prefix"
                    />
                    <p className="text-xs text-slate-500">T.ex. ART, P, PROD (lämna tomt för endast nummer)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Nästa nummer</Label>
                    <Input
                      type="number"
                      min="1"
                      value={articleSettings.next_article_number}
                      onChange={(e) => setArticleSettings({ ...articleSettings, next_article_number: parseInt(e.target.value) || 1 })}
                      data-testid="article-next-number"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Nummerformat</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md"
                      value={articleSettings.number_format}
                      onChange={(e) => setArticleSettings({ ...articleSettings, number_format: e.target.value })}
                    >
                      <option value="prefix_number">Prefix + Nummer (ART1, ART2, ART3...)</option>
                      <option value="number_only">Endast nummer (1, 2, 3...)</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded border">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Förhandsgranskning: </span>
                    <span className="font-mono text-indigo-600">
                      {getNumberPreview(articleSettings.article_prefix, articleSettings.next_article_number, articleSettings.number_format)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Default Values */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Standardvärden för nya artiklar</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Standard momssats</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md"
                      value={articleSettings.default_vat_rate}
                      onChange={(e) => setArticleSettings({ ...articleSettings, default_vat_rate: parseInt(e.target.value) })}
                    >
                      <option value={25}>25%</option>
                      <option value={12}>12%</option>
                      <option value={6}>6%</option>
                      <option value={0}>0% (momsfri)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Standard enhet</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md"
                      value={articleSettings.default_unit}
                      onChange={(e) => setArticleSettings({ ...articleSettings, default_unit: e.target.value })}
                    >
                      <option value="st">Styck</option>
                      <option value="timmar">Timmar</option>
                      <option value="m">Meter</option>
                      <option value="m2">Kvadratmeter</option>
                      <option value="kg">Kilogram</option>
                      <option value="l">Liter</option>
                      <option value="paket">Paket</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Standard försäljningskonto</Label>
                    <Input
                      value={articleSettings.default_account}
                      onChange={(e) => setArticleSettings({ ...articleSettings, default_account: e.target.value })}
                      placeholder="3010"
                      data-testid="article-default-account"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveArticleSettings} data-testid="save-article-settings">
                  <Save size={16} className="mr-2" />
                  Spara inställningar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Settings */}
        <TabsContent value="customer-settings" className="space-y-6 mt-6">
          <Card className="card-base" data-testid="customer-settings-card">
            <CardHeader>
              <CardTitle className="text-lg">Kundinställningar</CardTitle>
              <CardDescription>Anpassa kundnumrering och standardvärden för nya kunder</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Number Series Section */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <h4 className="font-medium text-green-900 flex items-center gap-2 mb-3">
                  <UserCircle size={16} />
                  Nummerserie för kunder
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kundprefix</Label>
                    <Input
                      value={customerSettings.customer_prefix}
                      onChange={(e) => setCustomerSettings({ ...customerSettings, customer_prefix: e.target.value })}
                      placeholder="K"
                      data-testid="customer-prefix"
                    />
                    <p className="text-xs text-slate-500">T.ex. K, KUND, C (lämna tomt för endast nummer)</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Nästa nummer</Label>
                    <Input
                      type="number"
                      min="1"
                      value={customerSettings.next_customer_number}
                      onChange={(e) => setCustomerSettings({ ...customerSettings, next_customer_number: parseInt(e.target.value) || 1 })}
                      data-testid="customer-next-number"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Nummerformat</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md"
                      value={customerSettings.number_format}
                      onChange={(e) => setCustomerSettings({ ...customerSettings, number_format: e.target.value })}
                    >
                      <option value="prefix_number">Prefix + Nummer (K1, K2, K3...)</option>
                      <option value="number_only">Endast nummer (1, 2, 3...)</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded border">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Förhandsgranskning: </span>
                    <span className="font-mono text-green-600">
                      {getNumberPreview(customerSettings.customer_prefix, customerSettings.next_customer_number, customerSettings.number_format)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Default Values */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Standardvärden för nya kunder</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Standard betalningsvillkor (dagar)</Label>
                    <Input
                      type="number"
                      value={customerSettings.default_payment_terms}
                      onChange={(e) => setCustomerSettings({ ...customerSettings, default_payment_terms: parseInt(e.target.value) || 30 })}
                      data-testid="customer-default-payment-terms"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Standard villkorstyp</Label>
                    <select
                      className="w-full h-10 px-3 border rounded-md"
                      value={customerSettings.default_payment_type}
                      onChange={(e) => setCustomerSettings({ ...customerSettings, default_payment_type: e.target.value })}
                    >
                      <option value="net_days">Netto dagar</option>
                      <option value="end_of_month">Löpande månad</option>
                      <option value="prepaid">Förskott</option>
                      <option value="on_delivery">Vid leverans</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveCustomerSettings} data-testid="save-customer-settings">
                  <Save size={16} className="mr-2" />
                  Spara inställningar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Companies Tab */}
        <TabsContent value="companies" className="space-y-6 mt-6">
          {activeCompany && (
            <Card className="card-base" data-testid="active-company">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Aktivt företag</CardTitle>
                    <CardDescription>Det företag du för närvarande arbetar med</CardDescription>
                  </div>
                  <Badge className="badge-success">Aktivt</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Företagsnamn</p>
                    <p className="font-medium">{activeCompany.name}</p>
                  </div>
                  {activeCompany.org_number && (
                    <div>
                      <p className="text-sm text-slate-500">Organisationsnummer</p>
                      <p className="font-medium font-mono">{activeCompany.org_number}</p>
                    </div>
                  )}
                  {activeCompany.city && (
                    <div>
                      <p className="text-sm text-slate-500">Ort</p>
                      <p className="font-medium">{activeCompany.city}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="card-base">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Alla företag ({companies.length})</CardTitle>
                <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-company-btn">
                      <Plus size={16} className="mr-2" />
                      Lägg till företag
                    </Button>
                  </DialogTrigger>
                  <DialogContent size="default">
                    <DialogHeader>
                      <DialogTitle>Lägg till företag</DialogTitle>
                      <DialogDescription>Skapa ett nytt företag att bokföra för</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateCompany} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="org_number">Organisationsnummer</Label>
                        <Input
                          id="org_number"
                          value={companyForm.org_number}
                          onChange={(e) => handleNewCompanyOrgChange(e.target.value)}
                          placeholder="556123-4567"
                          data-testid="org-number-input"
                        />
                        <p className="text-xs text-slate-500">Info hämtas automatiskt när du skriver 10 siffror</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_name">Företagsnamn *</Label>
                        <Input
                          id="company_name"
                          value={companyForm.name}
                          onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                          placeholder="Mitt Företag AB"
                          required
                          data-testid="company-name-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="address">Adress</Label>
                          <Input
                            id="address"
                            value={companyForm.address}
                            onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                            placeholder="Storgatan 1"
                            data-testid="address-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="city">Ort</Label>
                          <Input
                            id="city"
                            value={companyForm.city}
                            onChange={(e) => setCompanyForm({ ...companyForm, city: e.target.value })}
                            placeholder="Stockholm"
                            data-testid="city-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="postal_code">Postnummer</Label>
                        <Input
                          id="postal_code"
                          value={companyForm.postal_code}
                          onChange={(e) => setCompanyForm({ ...companyForm, postal_code: e.target.value })}
                          placeholder="111 22"
                          data-testid="postal-code-input"
                        />
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setCompanyDialogOpen(false)}>
                          Avbryt
                        </Button>
                        <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-company-btn">
                          Skapa företag
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {companies.length > 0 ? (
                <div className="space-y-3">
                  {companies.map((company) => (
                    <div
                      key={company.id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        company.id === user?.active_company_id
                          ? "border-indigo-200 bg-indigo-50"
                          : "border-slate-200"
                      }`}
                      data-testid={`company-item-${company.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          company.id === user?.active_company_id
                            ? "bg-indigo-600"
                            : "bg-slate-100"
                        }`}>
                          <Building2 className={company.id === user?.active_company_id ? "text-white" : "text-slate-500"} size={20} />
                        </div>
                        <div>
                          <p className="font-medium">{company.name}</p>
                          {company.org_number && (
                            <p className="text-sm text-slate-500 font-mono">{company.org_number}</p>
                          )}
                        </div>
                      </div>
                      {company.id === user?.active_company_id && (
                        <Badge className="badge-success">Aktivt</Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Building2 className="mx-auto mb-4 text-slate-300" size={48} />
                  <p>Inga företag skapade ännu</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6 mt-6">
          <Card className="card-base">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Användare</CardTitle>
                  <CardDescription>Hantera användare med åtkomst till företaget</CardDescription>
                </div>
                {user?.role === "admin" && (
                  <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="invite-user-btn">
                        <Plus size={16} className="mr-2" />
                        Bjud in användare
                      </Button>
                    </DialogTrigger>
                    <DialogContent size="default">
                      <DialogHeader>
                        <DialogTitle>Bjud in användare</DialogTitle>
                        <DialogDescription>Bjud in en användare till det aktiva företaget</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleInviteUser} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="invite_email">E-postadress</Label>
                          <Input
                            id="invite_email"
                            type="email"
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                            placeholder="kollega@foretag.se"
                            required
                            data-testid="invite-email-input"
                          />
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                            Avbryt
                          </Button>
                          <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-invite-btn">
                            <Mail size={16} className="mr-2" />
                            Skicka inbjudan
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {users.length > 0 ? (
                <div className="space-y-3">
                  {users.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-slate-200"
                      data-testid={`user-item-${u.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center">
                          <span className="text-white font-medium">
                            {u.name?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.role === "admin" && (
                          <Badge className="badge-pending">
                            <Shield size={12} className="mr-1" />
                            Admin
                          </Badge>
                        )}
                        {u.id === user?.id && (
                          <Badge className="badge-success">Du</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="mx-auto mb-4 text-slate-300" size={48} />
                  <p>Inga användare att visa</p>
                  {!user?.active_company_id && (
                    <p className="text-sm mt-1">Välj eller skapa ett företag först</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
